import {
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    FormControl,
    Grid,
    ListItemText,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import * as Yup from "yup";
import dayjs from "dayjs";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {
    ID_ACCESSORIES,
    ID_BE_FREE_EVO, ID_CAR_BOX,
    ID_CAR_CLOUD,
    ID_COMMISSIONS,
    ID_DAMAGE, ID_DAMAGE_SHEET, ID_FLEX_RENT,
    ID_FUEL,
    ID_TRANSFERS,
    ID_VOUCHERS
} from "../constants/PrintoutTypes";
import {useMobilityStoreRepository} from "../../../api/repositories/MobilityStoreRepository";
import {getLiteralMonth, getLiteralMonths, getPastYears} from "../../../utils/FinanceDateUtils";
import {SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import FormAsyncAutocomplete from "../../../experiments/forms/FormAsyncAutocomplete";
import {useNavigate} from "react-router-dom";
import FinanceDialog from "../../../component/FinanceDialog";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {MobilityStore} from "../constants/MobilityStoreModels";


interface PrintoutInput {
    year: number,
    month: number,
    types: string[],
    store: string[],
    legalEntity: string,
    dealer: string
}

const CreatePrintout = () => {

    const {t} = useTranslation("finance", {keyPrefix: 'mobility-store'});
    const { t: tg } = useTranslation("translation");

    const { postPrintoutRequest, checkClusterMS } = useMobilityStoreRepository();

    const { dispatchGlobalData, globalData: { user } } = useContext(GlobalContext);
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        }));


    const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
    }));


    const [stores, setStores] = useState<MobilityStore[]>([]);

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [printoutRequest, setPrintoutRequest] = useState<any>(undefined);
    const [printoutResponse, setPrintoutResponse] = useState<any>(undefined);
    const [allSelected, setAllSelected] = useState(true);

    const [focusStoreSelect, setFocusStoreSelect] = useState<boolean>(false);
    const navigate = useNavigate();

    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const types = [
        {
            label: t("printout-types." + ID_ACCESSORIES),
            value: ID_ACCESSORIES
        },
        {
            label: t('printout-types.' + ID_FUEL),
            value: ID_FUEL
        },
        {
            label: t('printout-types.' + ID_COMMISSIONS),
            value: ID_COMMISSIONS
        },
        {
            label: t('printout-types.' + ID_TRANSFERS),
            value: ID_TRANSFERS
        },
        {
            label: t('printout-types.' + ID_DAMAGE),
            value: ID_DAMAGE
        },
        {
            label: t('printout-types.' + ID_BE_FREE_EVO),
            value: ID_BE_FREE_EVO
        },
        {
            label: t('printout-types.' + ID_CAR_CLOUD),
            value: ID_CAR_CLOUD
        },
        {
            label: t('printout-types.' + ID_VOUCHERS),
            value: ID_VOUCHERS
        },
        {
            label: t("printout-types." + ID_FLEX_RENT),
            value: ID_FLEX_RENT
        },
        {
            label: t("printout-types." + ID_CAR_BOX),
            value: ID_CAR_BOX
        },
        {
            label: t("printout-types." + ID_DAMAGE_SHEET),
            value: ID_DAMAGE_SHEET
        }
    ];

    const schema = Yup.object().shape({
        year: Yup.number().required(),
        month: Yup.number().required(),
        types: Yup.array().test("not empty", (val) => val !== undefined && val.length > 0),
        store: Yup.array().required(),
        legalEntity: Yup.string().required(),
        dealer: Yup.string().required()
    });

    const defaultValues = {
        year: dayjs().month() > 0 ? dayjs().year() : dayjs().year() - 1,
        month: dayjs().month() > 0 ? dayjs().month() - 1 : 11,
        types: types.map((it: any) => it.value),
        store: [] as Array<any>,
        legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
        dealer: dealerOptions.length == 1 ? dealerOptions[0].value : "",
    }

    const {control, reset, watch, setValue, clearErrors, handleSubmit} = useForm<PrintoutInput>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    const formatClusters = (obj: object): string => {
        return Object.entries(obj)
            .map(([key, value]) => `\n ${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('; ');
    };

    const watcher = watch();

    function getPrintoutRequest(data: PrintoutInput): any {
        let finalYear = data.year || 0;
        let finalMonth = getLiteralMonth(data.month || 0);
        let finalTypes = data.types;
        let finalStores: any[] = data.store.length === 0 ? stores : data.store.map((it: any) => stores.find(store => store.sapId === it.value.sapId));

        return {
            year: finalYear,
            month: finalMonth,
            printoutTypes: finalTypes,
            mobilityStores: finalStores
       , legalEntity: data.legalEntity, dealer: data.dealer };
    }

    function sendPrintoutRequest(printoutRequest: any) {
        postPrintoutRequest(printoutRequest)
            .then((data) => {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('printout-wizard.snackbars.success'),
                                type: snackbarTypes.success,
                                show: true,
                                autoClose: true
                            }
                        }
                    });
                    navigate('/finance/mobility-store');
                }
            );
    }

    const onSubmit: SubmitHandler<PrintoutInput> = data => {
        let printoutRequest = getPrintoutRequest(data);

        checkClusterMS(printoutRequest).then(response => {
            if (response.result.stores.length !== 0 || response.result.voices.length !== 0 || response.result?.clustersWithoutFixedVoices?.TRANSFERS?.length !== 0) {
                setOpenModal(true)
                setPrintoutRequest(printoutRequest);
                setPrintoutResponse(response.result);
            } else {
                sendPrintoutRequest(printoutRequest);
            }
        })
    }

    const cleanForm = () => {
        reset(defaultValues);
        clearErrors();
    }

    useEffect(() => {
        if (watcher.types.length < types.length)
            setAllSelected(false)
        if (watcher.types.length === types.length)
            setAllSelected(true)
    }, [watcher.types])

    useEffect(() => {
        setMobilityStoreOptions(legalEntityOptions[0].value)
    }, [legalEntityOptions.length ===1])


    function sortStores(stores: any[]) {
        if (!Array.isArray(stores) || !stores.every(store => store.hasOwnProperty('name'))) {
            return;
        }
        stores.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            setStores([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
            return {
                label: tg(`dealers.${x.name}`),
                value: x.name,
            };
            });
        setDealers(dealers);
        setMobilityStoreOptions(value)
    }

    function setMobilityStoreOptions(value: any) {
        const mobilityStores: MobilityStore[] = (user.mobilityStores || [])
            .filter((dms: any)=> dms.representativeInfo && dms.financialInfo?.feesDued && dms.tenant == value)
            .map((dms: any) => {
                return {
                    id: dms.id,
                    tenant: dms.tenant,
                    name: dms.name,
                    code: dms.identificationCode,
                    type: dms.type,
                    sapId: dms.sapId,
                    msVendorId: dms.representativeInfo.idSapRepresentative,
                    msVendorName: dms.representativeInfo.companyName || dms.representativeInfo.nameAndSurnameOfTheContactPerson,
                    msVendorEmail: dms.representativeInfo.emailOfRepresentative
                }
            })

        setValue('store', []);

        sortStores(mobilityStores);
        setStores(mobilityStores);
    }

    return (
        <Card variant="outlined" sx={{margin: '15px'}}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent>
                    <Grid container spacing={2} alignItems={'flex-end'}>
                        <Grid item xs={12}>
                            <Typography variant={'body2'} color={'primary.light'} fontWeight={500}>
                                {t("printout-wizard.form.header")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant={'body2'} color={'neutral.dark'} fontWeight={700}>
                                {t("printout-wizard.form.select-criteria-label")}
                            </Typography>
                        </Grid>
                            <Grid item md={3} sm={3} xs={12}>
                            <FormControl variant="standard" style={{width: "100%"}}>
                                <FormSelect
                                    name="year"
                                    control={control}
                                    props={{
                                        label: t('printout-wizard.form.year-label'),
                                        onChange: (e) => {
                                            if (dayjs().year() === parseInt(e.target.value)) {
                                                setValue('month', dayjs().month() - 1);
                                            }
                                        }
                                    }}
                                    items={getPastYears(10, dayjs().month() === 0 ? dayjs().year() - 1 : dayjs().year()).reverse().map((it: number) => (
                                        <MenuItem key={it} value={it}>
                                            {it}
                                        </MenuItem>
                                    ))}
                                />
                            </FormControl>
                        </Grid>

                            <Grid item md={3} sm={3} xs={12}>
                            <FormControl variant="standard" style={{width: "100%"}}>
                                <FormSelect
                                    control={control}
                                    name="month"
                                    props={{
                                        variant: 'standard',
                                        label: t('printout-wizard.form.month-label')
                                    }}
                                    items={
                                        getLiteralMonths().map((it: string, index) => (
                                            <MenuItem key={it} value={index}
                                                      disabled={watcher.year === dayjs().year() && index > dayjs().month()}>
                                                {it.charAt(0).toUpperCase() + it.slice(1)}
                                            </MenuItem>
                                        ))
                                    }
                                />
                            </FormControl>
                        </Grid>

                            <Grid item md={3} sm={3} xs={12}>
                                <FormSelect
                                    name={"legalEntity"}
                                    control={control}
                                    props={{
                                        label: tg("shared.legalEntity"),
                                        fullWidth: true,
                                        required: true,
                                        onChange: (e: any) => {
                                            handleChangeLegalEntities(e.target.value)
                                        },
                                    }}

                                    items={legalEntityOptions.map((option: any) => (
                                        <MenuItem key={option.value}
                                                    value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                />
                            </Grid>
                            <Grid item md={3} sm={3} xs={12}>
                            <FormSelect
                                    name={"dealer"}
                                    control={control}
                                    props={{
                                        label: tg("shared.dealer"),
                                        fullWidth: true,
                                        required: true,
                                        disabled: dealers.length == 0
                                    }}
                                    items={dealers.map((option: any) => (
                                        <MenuItem key={option.value}
                                                    value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                />
                            </Grid>


                            <Grid item md={6} sm={6} xs={12}>
                            <FormSelect
                                control={control}
                                name="types"
                                props={{
                                    label: t('printout-wizard.form.types-label'),
                                    style: {width: "100%"},
                                    SelectProps: {
                                        multiple: true,
                                        renderValue: (selected: any) => selected.map((s: any) => types.find(ss => ss.value === s)!.label).join(', ')
                                    },
                                }}
                                items={[
                                    (<MenuItem onClick={() => {
                                        if (allSelected) {
                                            setValue('types', []);
                                        } else {
                                            setValue('types', types.map((t) => t.value))
                                        }
                                    }}>
                                        <Checkbox checked={allSelected}/>
                                        <ListItemText primary={t('printout-wizard.form.clear-types')}/>
                                    </MenuItem>)
                                ].concat(types.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Checkbox checked={watcher.types.indexOf(type.value) > -1}/>
                                        <ListItemText primary={type.label}/>
                                    </MenuItem>
                                )))}
                            />
                        </Grid>
                            <Grid item md={6} sm={6} xs={12}>
                            <FormAsyncAutocomplete
                                name={'store'}
                                control={control}
                                props={{
                                    initialValues: true,
                                    multiselect: true,
                                    getOptionLabel: (it) => `${it.value.name} | (${it.value.sapId})`,
                                    callback: () => Promise.resolve(() => {
                                        return stores.map(it => {
                                            return {key: it.sapId, value: it}
                                        })
                                    }),
                                    renderInput: (params) => <TextField
                                        {...params}
                                        onFocus={() => setFocusStoreSelect(true)}
                                        onBlur={() => setFocusStoreSelect(false)}
                                        label={watcher.store.length === 0 && !focusStoreSelect ? t('printout-wizard.form.store-label-all') : t('printout-wizard.form.store-label')}
                                    />
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{justifyContent: 'flex-end'}}>
                    <Button variant={"outlined"} color={"accent"} onClick={() => {
                        cleanForm();
                        navigate("/finance/mobility-store/printouts")
                    }}>{t('printout-wizard.buttons.cancel')}</Button>
                    <Button color={"accent"} type="submit">{t('printout-wizard.buttons.submit')}</Button>
                </CardActions>
            </form>
            <FinanceDialog open={openModal}
                           onClose={() => setOpenModal(false)}
                           onCancel={() => navigate("/finance/clusters")}
                           callback={() => {
                               if (printoutRequest) {
                                   setOpenModal(false);
                                   sendPrintoutRequest(printoutRequest);
                               }
                           }}
                           title={t('printout-wizard.dialog.title')}
                               content={t('printout-wizard.dialog.content', {
                                   contentStores: printoutResponse?.stores.length !== 0 ? t('printout-wizard.dialog.content-stores', {
                                       amount: printoutResponse?.stores.length,
                                       array: printoutResponse?.stores.join(', ')
                                   }) : '',
                                   contentVoices: printoutResponse?.voices.length !== 0 ? t('printout-wizard.dialog.content-voices', {
                                       amount: printoutResponse?.voices.length,
                                       array: printoutResponse?.voices.join(', ')
                                   }) : '',
                                   clustersWithoutFixedVoices: printoutResponse?.clustersWithoutFixedVoices?.TRANSFERS?.length > 0
                                       ? t('printout-wizard.dialog.content-fixed-voices', {
                                           amount: printoutResponse.clustersWithoutFixedVoices.TRANSFERS.length,
                                           array: formatClusters({ TRANSFERS: printoutResponse.clustersWithoutFixedVoices.TRANSFERS })
                                       }) : '',
                               })}
                           cancelButton={t('printout-wizard.dialog.left-button')}
                           confirmButton={t('printout-wizard.dialog.right-button')}
            />
        </Card>
    )
}
export default CreatePrintout;